import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Inbox, Refresh } from "@mui/icons-material";
import IncidentListItem from "./IncidentListItem";
import type { Incident } from "../../../../types/incident";
import { LIST_PAGE_SIZES } from "../../lib/constants";
import IconAutocomplete from "../../../../components/forms/IconAutocomplete";
import { PAGE_SIZE_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import { useTranslation } from "react-i18next";

type IncidentListPanelProps = {
  loading: boolean;
  incidents: Incident[];
  filteredCount: number;
  selectedIncidentId?: string;
  onSelectIncident: (incident: Incident) => void;
  onRefresh: () => void;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (size: number) => void;
  folderLabel: string;
};

export default function IncidentListPanel({
  loading,
  incidents,
  filteredCount,
  selectedIncidentId,
  onSelectIncident,
  onRefresh,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  folderLabel,
}: IncidentListPanelProps) {
  const { t } = useTranslation();
  const startIndex = (currentPage - 1) * itemsPerPage;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedIncidentId || !listRef.current) return;
    const selectedEl = listRef.current.querySelector(
      `[data-incident-id="${selectedIncidentId}"]`,
    );
    selectedEl?.scrollIntoView({ block: "nearest" });
  }, [selectedIncidentId, incidents, currentPage]);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 360, lg: 400 },
        flex: { xs: "1 1 auto", md: "0 0 auto" },
        minHeight: 0,
        height: { xs: "auto", md: "100%" },
        maxHeight: "100%",
        alignSelf: "stretch",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight={700}>
            {folderLabel}
          </Typography>
          <IconButton size="small" onClick={onRefresh} aria-label={t("common.refresh")}>
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: (t) => t.palette.action.hover,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {filteredCount} incident{filteredCount === 1 ? "" : "s"}
        </Typography>
      </Box>

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Loading incidents…
            </Typography>
          </Box>
        ) : incidents.length === 0 ? (
          <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
            <Inbox sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No incidents match your filters
            </Typography>
          </Box>
        ) : (
          incidents.map((incident) => (
            <IncidentListItem
              key={incident.id}
              incident={incident}
              selected={selectedIncidentId === incident.id}
              onSelect={onSelectIncident}
            />
          ))
        )}
      </Box>

      {filteredCount > 0 && (
        <Box
          sx={{
            px: 1.25,
            py: 1,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredCount)} / {filteredCount}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => onPageChange(page)}
              size="small"
              siblingCount={0}
              boundaryCount={1}
              color="primary"
            />
            <Box sx={{ minWidth: 88 }}>
              <IconAutocomplete
                options={PAGE_SIZE_OPTIONS.filter((option) =>
                  (LIST_PAGE_SIZES as readonly number[]).includes(option.value),
                )}
                value={itemsPerPage}
                onChange={(value) => {
                  if (value != null) onItemsPerPageChange(value);
                }}
                disableClearable
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
